import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const SECTIONS = [
  {
    h: "Overview",
    blocks: [
      { sub: "What is the return policy?", p: "We do not accept change-of-mind returns for any device, due to the 1-year repair warranty on the set-top box alone (excluding the remote). However, exceptions can be discussed on a case-by-case basis by raising a ticket with our support team. Approved returns incur a handling/restocking fee of $125. Accepted returns are issued a Return Authorization Number (RAN) by our support team, which must be provided to customer service to organise the return and refund (after deducting restocking and handling fees)." },
      { p: "To be eligible for a return, your item must be unused and in the same condition you received it, and in its original packaging." },
      { p: "If a fault is found in our product, we will replace it within the warranty period. If the fault is due to external factors such as your internet service, modem, router, or anything beyond our control, we will inform you accordingly." },
      { sub: "What is the return policy on recharges?", p: "Recharges are prepaid and, once used, are non-refundable. No refunds are provided on recharge transactions." },
      { sub: "What if the box becomes faulty after recharging?", p: "Recharge is credited only and is non-refundable. When you recharge you have the option to purchase an extended warranty for your device to cover such situations. An extended warranty can only be purchased at the time of recharge, not afterwards." },
      { p: "To complete your return, we require a receipt or proof of purchase." },
    ],
  },
  {
    h: "Refunds",
    blocks: [
      { p: "Once your return is received and inspected, we will email you to confirm we have received the item, and notify you whether your refund is approved or rejected." },
      { p: "If approved, your refund will be processed and a credit automatically applied to your credit card or original method of payment within a certain number of days." },
      { sub: "Late or missing refunds", p: "If you haven't received a refund yet, first re-check your bank account. Then contact your credit card company — it may take time before the refund is officially posted. Next, contact your bank, as there is often processing time before a refund posts. If you've done all of this and still have not received your refund, please contact us." },
      { sub: "Sale items", p: "Only regular-priced items may be refunded. Sale items cannot be refunded." },
    ],
  },
  {
    h: "Exchanges",
    blocks: [
      { p: "We only replace items if they are defective or damaged. If you need to exchange an item for the same item, please contact us." },
    ],
  },
  {
    h: "Shipping returns",
    blocks: [
      { p: "To return your product, mail it to us. You are responsible for paying your own shipping costs to return the item. Shipping costs are non-refundable. If you receive a refund, the cost of return shipping will be deducted from your refund." },
      { p: "Depending on where you live, the time for an exchanged product to reach you may vary. For more expensive items, consider a trackable shipping service or shipping insurance — we don't guarantee we will receive your returned item." },
    ],
  },
];

export default function Refunds() {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div className="bg-[#F8F9FA] min-h-screen">
      <header className="bg-[#F8F9FA]/80 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 font-display font-black text-lg tracking-tight uppercase" data-testid="logo">
            <img src="/logo.png?v=2" alt="Indian TV Box logo" className="w-9 h-9 rounded-lg" />
            <span>INDIANTV<span className="text-[#FF5722]">.</span>BOX</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-sm font-bold text-slate-500 hover:text-[#0F172A]" data-testid="back-btn"><ArrowLeft size={16} /> Back to store</Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-16" data-testid="refunds-page">
        <p className="text-xs font-black uppercase tracking-[0.25em] text-[#FF5722] mb-3">Policy</p>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl text-[#0F172A] leading-[0.95]">Refund & Returns Policy</h1>

        <div className="mt-12 space-y-12">
          {SECTIONS.map((s) => (
            <section key={s.h}>
              <h2 className="font-display font-bold text-2xl text-[#0F172A] border-b border-slate-200 pb-3 mb-5">{s.h}</h2>
              <div className="space-y-4">
                {s.blocks.map((b, i) => (
                  <div key={i}>
                    {b.sub && <h3 className="font-display font-bold text-base text-[#0F172A] mb-1">{b.sub}</h3>}
                    <p className="text-base font-medium text-slate-600 leading-relaxed">{b.p}</p>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <section className="bg-white border border-slate-200 rounded-3xl p-8">
            <h2 className="font-display font-bold text-2xl text-[#0F172A]">Need help?</h2>
            <p className="mt-3 text-base font-medium text-slate-600">Contact us for any questions related to refunds and returns.</p>
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm font-bold text-[#0F172A]">
              <a href="mailto:admin@indiantvbox.com.au" className="hover:text-[#FF5722]" data-testid="refunds-email">admin@indiantvbox.com.au</a>
              <a href="tel:+61412213515" className="hover:text-[#FF5722]" data-testid="refunds-phone">0412 213 515</a>
              <a href="https://wa.me/61412213515" target="_blank" rel="noopener noreferrer" className="hover:text-[#FF5722]">WhatsApp us</a>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
